import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { UIBlock } from "../../api/uiBlocksApi";
import { Eta } from "eta";

const EditorContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  border-left: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transform: translateX(${(props) => (props.hidden ? "100%" : "0")});
  transition: transform 0.3s ease-in-out;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
`;

const EditorHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e5e9;
  background: #f8f9fa;
`;

const EditorTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
`;

const BlockInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BlockName = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const CategoryBadge = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const EditorContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const EditorActions = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e5e9;
  background: #f8f9fa;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{
  variant?: "primary" | "secondary" | "danger";
}>`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      case "danger":
        return `
          background: #ef4444;
          color: white;
          &:hover {
            background: #dc2626;
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
          }
        `;
    }
  }}
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ColorInput = styled.input`
  padding: 4px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  width: 80px;
  height: 40px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`;

const TabContainer = styled.div`
  border-bottom: 1px solid #e1e5e9;
  margin-bottom: 20px;
`;

const TabList = styled.div`
  display: flex;
  gap: 0;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 16px;
  border: none;
  background: none;
  border-bottom: 2px solid
    ${(props) => (props.active ? "#3b82f6" : "transparent")};
  color: ${(props) => (props.active ? "#3b82f6" : "#6b7280")};
  font-weight: ${(props) => (props.active ? 600 : 400)};
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    color: #3b82f6;
  }
`;

const PreviewContainer = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
`;

const PreviewTitle = styled.h5`
  margin: 0 0 12px 0;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
`;

const PreviewContent = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #6b7280;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
`;

interface BlockEditorProps {
  block: UIBlock | null;
  overrides: Record<string, any>;
  customHtml?: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (overrides: Record<string, any>, customHtml?: string) => void;
  onHtmlEdit: () => void;
}

export const BlockEditor: React.FC<BlockEditorProps> = ({
  block,
  overrides,
  customHtml,
  isOpen,
  onClose,
  onSave,
  onHtmlEdit,
}) => {
  const [activeTab, setActiveTab] = useState<"content" | "settings">("content");
  const [localOverrides, setLocalOverrides] = useState<Record<string, any>>({});

  useEffect(() => {
    setLocalOverrides(overrides || {});
  }, [overrides, block]);

  if (!block) return null;

  const handleInputChange = (field: string, value: any) => {
    setLocalOverrides((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    onSave(localOverrides, customHtml);
    onClose();
  };

  const handleCancel = () => {
    setLocalOverrides(overrides || {});
    onClose();
  };

  // Extract template variables from HTML content
  const getTemplateVariables = (htmlContent: string): string[] => {
    const regex = /{{\s*[=~]\s*(\w+)/g;
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(htmlContent)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    return variables;
  };

  const templateVariables = getTemplateVariables(block.html);

  // Generate preview HTML with current overrides
  const generatePreview = () => {
    let preview = customHtml || block.html;

    // Create template data with defaults and overrides
    const templateData: Record<string, any> = {};

    // Extract default values from schema
    Object.entries(block.schema.editableFields || {}).forEach(
      ([key, defaultValue]) => {
        templateData[key] = defaultValue;
      }
    );

    // Apply current overrides
    Object.entries(localOverrides).forEach(([key, value]) => {
      templateData[key] = value;
    });

    // Use Eta template engine to render preview
    try {
      const eta = new (Eta as any)({
        tags: ["{{", "}}"],
        useWith: true,
        autoEscape: false,
      });
      return eta.renderString(preview, templateData);
    } catch (error) {
      console.error("Error rendering preview:", error);

      // Fallback: simple string replacement
      templateVariables.forEach((variable) => {
        const value =
          localOverrides[variable] || templateData[variable] || `[${variable}]`;
        const regex = new RegExp(`{{\\s*[=~]\\s*${variable}\\s*}}`, "g");
        preview = preview?.replace(regex, String(value));
      });

      return preview;
    }
  };

  return (
    <EditorContainer hidden={!isOpen}>
      <EditorHeader>
        <CloseButton onClick={onClose}>×</CloseButton>
        <EditorTitle>Edit Block</EditorTitle>
        <BlockInfo>
          <BlockName>{block.name}</BlockName>
          <CategoryBadge>{block.category}</CategoryBadge>
        </BlockInfo>
      </EditorHeader>

      <TabContainer>
        <TabList>
          <Tab
            active={activeTab === "content"}
            onClick={() => setActiveTab("content")}
          >
            Content
          </Tab>
          <Tab
            active={activeTab === "settings"}
            onClick={() => setActiveTab("settings")}
          >
            Settings
          </Tab>
        </TabList>
      </TabContainer>

      <EditorContent>
        {activeTab === "content" && (
          <>
            <FormSection>
              <SectionTitle>Content Overrides</SectionTitle>
              <HelpText>
                Customize the content for this block. These values will replace
                the template variables in the HTML.
              </HelpText>

              {templateVariables.length === 0 ? (
                <HelpText>
                  This block doesn't have any customizable content variables.
                </HelpText>
              ) : (
                templateVariables.map((variable) => {
                  const currentValue = localOverrides[variable] || "";

                  return (
                    <FormGroup key={variable}>
                      <Label>
                        {variable.charAt(0).toUpperCase() + variable.slice(1)}
                      </Label>
                      {variable.toLowerCase().includes("text") ||
                      variable.toLowerCase().includes("content") ||
                      variable.toLowerCase().includes("description") ||
                      variable.toLowerCase().includes("subtitle") ? (
                        <Textarea
                          value={currentValue}
                          onChange={(e) =>
                            handleInputChange(variable, e.target.value)
                          }
                          placeholder={`Enter ${variable}...`}
                        />
                      ) : variable.toLowerCase().includes("color") ? (
                        <ColorInput
                          type="color"
                          value={currentValue || "#000000"}
                          onChange={(e) =>
                            handleInputChange(variable, e.target.value)
                          }
                        />
                      ) : (
                        <Input
                          type="text"
                          value={currentValue}
                          onChange={(e) =>
                            handleInputChange(variable, e.target.value)
                          }
                          placeholder={`Enter ${variable}...`}
                        />
                      )}
                    </FormGroup>
                  );
                })
              )}
            </FormSection>

            <FormSection>
              <SectionTitle>Advanced</SectionTitle>
              <ActionButton onClick={onHtmlEdit}>Edit HTML Code</ActionButton>
              <HelpText style={{ marginTop: "8px" }}>
                Directly edit the HTML code for complete customization.
              </HelpText>
            </FormSection>

            <PreviewContainer>
              <PreviewTitle>Preview</PreviewTitle>
              <PreviewContent>{generatePreview()}</PreviewContent>
            </PreviewContainer>
          </>
        )}

        {activeTab === "settings" && (
          <FormSection>
            <SectionTitle>Block Settings</SectionTitle>
            <FormGroup>
              <Label>Block Name</Label>
              <Input
                type="text"
                value={block.name}
                disabled
                style={{ background: "#f9fafb", color: "#6b7280" }}
              />
            </FormGroup>
            <FormGroup>
              <Label>Category</Label>
              <Input
                type="text"
                value={block.category}
                disabled
                style={{ background: "#f9fafb", color: "#6b7280" }}
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <Textarea
                value={block.description}
                disabled
                style={{ background: "#f9fafb", color: "#6b7280" }}
              />
            </FormGroup>
            {block.isPremium && (
              <HelpText>
                ⭐ This is a premium block. Advanced features may require a paid
                subscription.
              </HelpText>
            )}
          </FormSection>
        )}
      </EditorContent>

      <EditorActions>
        <ActionButton onClick={handleCancel}>Cancel</ActionButton>
        <ActionButton variant="primary" onClick={handleSave}>
          Save Changes
        </ActionButton>
      </EditorActions>
    </EditorContainer>
  );
};
